import classNames from 'classnames';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { DemoPill, StateMessage } from '@/components/ui/controls';
import { NotYet, NotYetInfo, notYet } from '@/components/ui/not-yet';
import { Badge, Card, PageHeader } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import { StageTag } from '@/components/ui/stage';
import { btn, table } from '@/components/ui/styles';
import { formatDate, formatDateTime, leadTitle, shownEmail } from '@/utils/format';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';
import { dateValue, useSort, type SortColumns } from '@/utils/use-sort';

import type { StageSuggestion } from './suggestion-types';

/** Marks a suggestion the mock wrote: keywords, not an AI (Q10). */
export const MockSuggesterPill = () => (
  <DemoPill title="Written by the mock suggester from keywords, not by an AI, until the AI provider is chosen (open question Q10).">
    Mock suggester
  </DemoPill>
);

/**
 * Weekly AI stage suggestions (CAS-5, TG-5): each week the suggester reads every lead's
 * linked conversations and proposes a stage; a person accepts or rejects it. Accepting
 * Clarifications sets that stage, and accepting a blocker tags the lead Blocked (Q4); accepting a platform stage
 * records the decision only, as those change from platform events alone (CAS-4). On a phone, Reject and Accept
 * share the card's width, and the decided table folds into its first column (UI-1, UI-2).
 */
// The suggested stage and the decision have their own badges; sorting by them adds little.
const DECIDED_COLUMNS: SortColumns<StageSuggestion> = {
  lead: { value: (s) => leadTitle(s.lead_name, s.lead_email) },
  by: { value: (s) => s.decided_by },
  when: { value: (s) => dateValue(s.decided_at), desc: true },
};

const SuggestionsPage = () => {
  const open = useApi<StageSuggestion[]>('/suggestions', { state: 'open' });
  const decided = useApi<StageSuggestion[]>('/suggestions', { state: 'decided' });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const decide = async (s: StageSuggestion, accept: boolean) => {
    setBusy(s.id);
    setError(null);
    try {
      await http.post(`/suggestions/${encodeURIComponent(s.id)}/${accept ? 'accept' : 'reject'}`);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      setError(status === 409 ? 'Someone has already decided that one.' : 'That did not work. Try again.');
    } finally {
      setBusy(null);
      open.reload();
      decided.reload();
    }
  };

  const pending = open.data ?? [];
  const { rows: done, sort, toggle } = useSort(decided.data ?? [], DECIDED_COLUMNS);

  return (
    <div className="max-w-4xl">
      <PageHeader
        back={{ to: '/leads', label: 'Leads' }}
        title="AI suggestions"
        description="Each week the AI reads the linked conversations and suggests a stage change. A person decides."
      />
      <div className="mb-6 flex items-start gap-3">
        <p className={classNames('min-w-0 flex-1 text-sm text-fg-muted', notYet)}>
          Accepting Clarifications sets that stage on the lead, and accepting Blocked tags it, in Configuring or Clarifications. Accepting New, Configuring, Paid or Live records the decision only:
          those come from platform events (CAS-4).
        </p>
        <NotYetInfo what="ai" className="mt-0.5" />
      </div>
      <NotYet what="ai" info={false}>
        {error && <p className="mb-3 text-sm text-danger">{error}</p>}

        <h2 className="mb-3 text-sm font-medium text-fg-strong">To review ({pending.length})</h2>
        {open.error && !open.data ? (
          <StateMessage title="Suggestions could not be loaded" tone="error">
            {open.error}
          </StateMessage>
        ) : !open.data ? (
          <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
        ) : pending.length ? (
          <div className="space-y-4">
            {pending.map((s) => (
              <Card key={s.id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/leads/${encodeURIComponent(s.lead_id)}`} className="inline-flex min-h-11 items-center break-words text-fg-strong hover:text-brand md:min-h-0">
                      {leadTitle(s.lead_name, s.lead_email)}
                    </Link>
                    <p className="break-words text-xs text-fg-muted">{shownEmail(s.lead_email)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 md:flex-nowrap">
                    {s.model === 'mock' && <MockSuggesterPill />}
                    <p className="text-xs text-fg-faint">
                      Week of {formatDate(s.created_at)} · {s.messages_read} {s.messages_read === 1 ? 'message' : 'messages'} read
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                  <StageTag stage={s.current_stage} />
                  <span className="text-fg-faint" aria-label="suggested">
                    →
                  </span>
                  <StageTag stage={s.suggested_stage} />
                </div>
                <p className="mt-3 break-words text-sm text-fg">{s.reason}</p>
                {s.quote && <blockquote className="mt-2 rounded-lg bg-surface-inset px-3 py-2 text-sm text-fg-muted">“{s.quote}”</blockquote>}
                <div className="mt-4 grid grid-cols-2 gap-2 md:flex md:justify-end">
                  <button type="button" className={btn.secondary} disabled={busy === s.id} onClick={() => decide(s, false)}>
                    Reject
                  </button>
                  <button type="button" className={btn.primary} disabled={busy === s.id} onClick={() => decide(s, true)}>
                    Accept
                  </button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <StateMessage title="Nothing to review">New suggestions arrive once a week, from the linked Telegram conversations.</StateMessage>
        )}

        {done.length > 0 && (
          <>
            <h2 className="mb-3 mt-8 text-sm font-medium text-fg-strong">Decided ({done.length})</h2>
            <div className={table.wrap}>
              <div className="overflow-x-auto">
                <table className={table.table}>
                  <thead className={table.head}>
                    <tr>
                      <SortTh label="Lead" column="lead" sort={sort} onSort={toggle} />
                      <th scope="col" className={`${table.th} hidden md:table-cell`}>Suggested</th>
                      <th scope="col" className={`${table.th} hidden md:table-cell`}>Decision</th>
                      <SortTh label="By" column="by" sort={sort} onSort={toggle} className="hidden md:table-cell" />
                      <SortTh label="When" column="when" sort={sort} onSort={toggle} className="hidden md:table-cell" />
                    </tr>
                  </thead>
                  <tbody>
                    {done.map((s) => (
                      <tr key={s.id} className={table.row}>
                        <td className={table.td}>
                          <Link to={`/leads/${encodeURIComponent(s.lead_id)}`} className="break-words text-fg hover:text-brand">
                            {leadTitle(s.lead_name, s.lead_email)}
                          </Link>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2 md:hidden">
                            <StageTag stage={s.suggested_stage} />
                            <Badge tone={s.state === 'accepted' ? 'ok' : 'neutral'}>{s.state === 'accepted' ? 'Accepted' : 'Rejected'}</Badge>
                          </div>
                          <p className="mt-1 break-words text-xs text-fg-muted md:hidden">
                            {s.decided_by || '—'}
                            {s.decided_at && ` · ${formatDateTime(s.decided_at)}`}
                          </p>
                        </td>
                        <td className={`${table.td} hidden md:table-cell`}>
                          <StageTag stage={s.suggested_stage} />
                        </td>
                        <td className={`${table.td} hidden md:table-cell`}>
                          <Badge tone={s.state === 'accepted' ? 'ok' : 'neutral'}>{s.state === 'accepted' ? 'Accepted' : 'Rejected'}</Badge>
                        </td>
                        <td className={`${table.td} hidden text-fg-muted md:table-cell`}>{s.decided_by || '—'}</td>
                        <td className={`${table.td} hidden whitespace-nowrap text-fg-muted md:table-cell`}>{s.decided_at ? formatDateTime(s.decided_at) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </NotYet>
    </div>
  );
};

export default SuggestionsPage;
